import type { Chat } from "@circle-react/types/CommunityInbox/Chat";
import type { Message } from "@circle-react/types/CommunityInbox/Message";

export const patchAgentEnabledInChats = (
  chats: Chat[],
  agentId: number,
  enabled: boolean,
  chatDisabledById?: Record<number, boolean>,
) => {
  chats.forEach(chat => {
    if (chat.community_bot_profile.id === agentId) {
      // When per-chat state is available (admin channel), trust it over the
      // agent-wide `enabled` flag: audience filters can leave some chats
      // disabled and others enabled for the very same agent.
      chat.disabled = chatDisabledById?.[chat.id] ?? !enabled;
      chat.community_bot_profile.enabled = enabled;
    }
  });
};

// Marks the admin chat read and returns the delta to apply to
// totalUnreadPausedAiChatsCount (chat may be paused-AI and thus counted there).
export const markAdminChatReadAndGetPausedDelta = (
  chats: Chat[],
  chatId: number,
): number => {
  const index = chats.findIndex(c => c.id === chatId);
  if (index === -1) {
    return 0;
  }
  if (!chats[index].has_unread_messages_for_admin) {
    return 0;
  }
  chats[index].has_unread_messages_for_admin = false;
  return chats[index].ai_snoozed ? -1 : 0;
};

// Marks the member chat read and returns the delta to apply to totalUnreadChatsCount.
export const markMemberChatReadAndGetUnreadDelta = (
  chats: Chat[],
  chatId: number,
): number => {
  const index = chats.findIndex(c => c.id === chatId);
  if (index === -1) {
    return 0;
  }

  if (!chats[index].has_unread_messages_for_member) {
    return 0;
  }

  chats[index].has_unread_messages_for_member = false;
  return -1;
};

export const applyUnreadFlagForNewMessage = (
  state: {
    memberChats: Chat[];
    adminChats: Chat[];
    totalUnreadChatsCount: number;
    totalUnreadPausedAiChatsCount: number;
  },
  message: Message,
  type: "member" | "admin",
) => {
  const chatsKeyName = type === "member" ? "memberChats" : "adminChats";
  const chatIndex = state[chatsKeyName].findIndex(
    c => c.id === message.community_inbox_chat_id,
  );
  if (chatIndex === -1) {
    return;
  }
  if (type === "member") {
    state.memberChats[chatIndex].has_unread_messages_for_member = true;
    state.totalUnreadChatsCount = state.memberChats.filter(
      chat => chat.has_unread_messages_for_member,
    ).length;
  } else {
    state.adminChats[chatIndex].has_unread_messages_for_admin = true;
    state.totalUnreadPausedAiChatsCount = state.adminChats.filter(
      chat => chat.has_unread_messages_for_admin && chat.ai_snoozed,
    ).length;
  }
};
